import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { LogOut, Plus, Pencil, Trash2, ArrowLeft, BookOpen, MessageSquare, Mail } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EMPTY_BOOK = { title: "", series: "Belfast Murders", description: "", cover_url: "", amazon_link: "", order: 0, is_latest: false };
const EMPTY_REVIEW = { reviewer_name: "", book_title: "", quote: "" };

function AdminLogin({ onLogin }) {
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/admin/login`, { password });
      sessionStorage.setItem("admin_token", data.token);
      onLogin(data.token);
      toast.success("Logged in successfully");
    } catch {
      toast.error("Invalid password");
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center px-4">
      <div className="glass-panel p-8 w-full max-w-sm">
        <h1 className="font-display text-2xl font-bold text-white mb-2">Admin Login</h1>
        <p className="text-zinc-500 text-sm mb-8">Enter password to manage content</p>
        <form onSubmit={handleLogin} className="space-y-4">
          <Input
            data-testid="admin-password-input"
            type="password"
            value={password}
            onChange={e => setPassword(e.target.value)}
            placeholder="Password"
            className="bg-zinc-900/50 border-white/10 text-white rounded-none h-12"
          />
          <Button
            type="submit"
            disabled={loading}
            data-testid="admin-login-btn"
            className="w-full bg-rose-700 hover:bg-rose-800 text-white rounded-none h-12"
          >
            {loading ? "Signing in..." : "Sign In"}
          </Button>
        </form>
        <a href="/" className="flex items-center gap-2 text-zinc-600 hover:text-zinc-400 text-sm mt-6 transition-colors">
          <ArrowLeft className="h-4 w-4" /> Back to site
        </a>
      </div>
    </div>
  );
}

export default function AdminPage() {
  const [token, setToken] = useState(sessionStorage.getItem("admin_token") || "");
  const [authenticated, setAuthenticated] = useState(false);
  const [books, setBooks] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [messages, setMessages] = useState([]);
  const [bookDialog, setBookDialog] = useState(false);
  const [reviewDialog, setReviewDialog] = useState(false);
  const [editingBook, setEditingBook] = useState(null);
  const [editingReview, setEditingReview] = useState(null);
  const [bookForm, setBookForm] = useState(EMPTY_BOOK);
  const [reviewForm, setReviewForm] = useState(EMPTY_REVIEW);

  const headers = { Authorization: `Bearer ${token}` };

  const fetchData = useCallback(async () => {
    try {
      const [b, r, m] = await Promise.all([
        axios.get(`${API}/books`, { headers }),
        axios.get(`${API}/reviews`, { headers }),
        axios.get(`${API}/contact`, { headers }),
      ]);
      setBooks(b.data);
      setReviews(r.data);
      setMessages(m.data);
    } catch {
      toast.error("Failed to fetch data");
    }
  }, [token]);

  useEffect(() => {
    if (!token) return;
    axios.get(`${API}/admin/verify`, { headers })
      .then(() => { setAuthenticated(true); fetchData(); })
      .catch(() => { sessionStorage.removeItem("admin_token"); setToken(""); });
  }, [token, fetchData]);

  const handleLogin = (t) => { setToken(t); };

  const logout = () => {
    sessionStorage.removeItem("admin_token");
    setToken("");
    setAuthenticated(false);
  };

  // Book CRUD
  const openBookDialog = (book = null) => {
    setEditingBook(book);
    setBookForm(book ? { title: book.title, series: book.series, description: book.description, cover_url: book.cover_url, amazon_link: book.amazon_link, order: book.order, is_latest: book.is_latest } : EMPTY_BOOK);
    setBookDialog(true);
  };

  const saveBook = async () => {
    if (!bookForm.title || !bookForm.series) { toast.error("Title and series are required"); return; }
    try {
      if (editingBook) {
        await axios.put(`${API}/books/${editingBook.id}`, bookForm, { headers });
        toast.success("Book updated");
      } else {
        await axios.post(`${API}/books`, bookForm, { headers });
        toast.success("Book added");
      }
      setBookDialog(false);
      fetchData();
    } catch { toast.error("Failed to save book"); }
  };

  const deleteBook = async (id) => {
    if (!window.confirm("Delete this book?")) return;
    try {
      await axios.delete(`${API}/books/${id}`, { headers });
      toast.success("Book deleted");
      fetchData();
    } catch { toast.error("Failed to delete book"); }
  };

  // Review CRUD
  const openReviewDialog = (review = null) => {
    setEditingReview(review);
    setReviewForm(review ? { reviewer_name: review.reviewer_name, book_title: review.book_title, quote: review.quote } : EMPTY_REVIEW);
    setReviewDialog(true);
  };

  const saveReview = async () => {
    if (!reviewForm.reviewer_name || !reviewForm.quote) { toast.error("Reviewer name and quote are required"); return; }
    try {
      if (editingReview) {
        await axios.put(`${API}/reviews/${editingReview.id}`, reviewForm, { headers });
        toast.success("Review updated");
      } else {
        await axios.post(`${API}/reviews`, reviewForm, { headers });
        toast.success("Review added");
      }
      setReviewDialog(false);
      fetchData();
    } catch { toast.error("Failed to save review"); }
  };

  const deleteReview = async (id) => {
    if (!window.confirm("Delete this review?")) return;
    try {
      await axios.delete(`${API}/reviews/${id}`, { headers });
      toast.success("Review deleted");
      fetchData();
    } catch { toast.error("Failed to delete review"); }
  };

  if (!authenticated) return <AdminLogin onLogin={handleLogin} />;

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-[#0a0a0a] text-white">
      {/* Header */}
      <div className="border-b border-white/10 px-4 md:px-8 py-4">
        <div className="container mx-auto max-w-7xl flex items-center justify-between">
          <div className="flex items-center gap-4">
            <h1 className="font-display text-xl font-bold">CMS Dashboard</h1>
            <Badge className="bg-rose-700/20 text-rose-500 border-0">Admin</Badge>
          </div>
          <div className="flex items-center gap-3">
            <a href="/" className="text-zinc-500 hover:text-white text-sm flex items-center gap-1 transition-colors">
              <ArrowLeft className="h-4 w-4" /> Site
            </a>
            <Button data-testid="admin-logout-btn" variant="ghost" size="sm" onClick={logout} className="text-zinc-500 hover:text-white">
              <LogOut className="h-4 w-4 mr-1" /> Logout
            </Button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="container mx-auto max-w-7xl px-4 md:px-8 py-8">
        <Tabs defaultValue="books">
          <TabsList className="bg-zinc-900/50 border border-white/10 rounded-none p-1 h-auto">
            <TabsTrigger data-testid="admin-tab-books" value="books" className="rounded-none data-[state=active]:bg-rose-700 data-[state=active]:text-white text-zinc-400 px-4 py-2 text-sm">
              <BookOpen className="h-4 w-4 mr-2" /> Books ({books.length})
            </TabsTrigger>
            <TabsTrigger data-testid="admin-tab-reviews" value="reviews" className="rounded-none data-[state=active]:bg-rose-700 data-[state=active]:text-white text-zinc-400 px-4 py-2 text-sm">
              <MessageSquare className="h-4 w-4 mr-2" /> Reviews ({reviews.length})
            </TabsTrigger>
            <TabsTrigger data-testid="admin-tab-messages" value="messages" className="rounded-none data-[state=active]:bg-rose-700 data-[state=active]:text-white text-zinc-400 px-4 py-2 text-sm">
              <Mail className="h-4 w-4 mr-2" /> Messages ({messages.length})
            </TabsTrigger>
          </TabsList>

          {/* Books Tab */}
          <TabsContent value="books" className="mt-6">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-lg font-bold">Manage Books</h2>
              <Button data-testid="add-book-btn" onClick={() => openBookDialog()} className="bg-rose-700 hover:bg-rose-800 rounded-none text-sm">
                <Plus className="h-4 w-4 mr-1" /> Add Book
              </Button>
            </div>
            <div className="space-y-2">
              {books.map(book => (
                <div key={book.id} data-testid={`admin-book-${book.id}`} className="flex items-center gap-4 bg-zinc-900/30 border border-white/5 p-3">
                  <img src={book.cover_url} alt={book.title} className="w-10 h-14 object-cover flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-white text-sm font-bold truncate">{book.title}</p>
                    <div className="flex items-center gap-2 mt-0.5">
                      <span className="text-zinc-500 text-xs">{book.series}</span>
                      {book.is_latest && <Badge className="bg-rose-700/20 text-rose-500 border-0 text-[10px] px-1.5 py-0">Latest</Badge>}
                    </div>
                  </div>
                  <div className="flex gap-1 flex-shrink-0">
                    <Button data-testid={`edit-book-${book.id}`} variant="ghost" size="icon" onClick={() => openBookDialog(book)} className="h-8 w-8 text-zinc-500 hover:text-white">
                      <Pencil className="h-3.5 w-3.5" />
                    </Button>
                    <Button data-testid={`delete-book-${book.id}`} variant="ghost" size="icon" onClick={() => deleteBook(book.id)} className="h-8 w-8 text-zinc-500 hover:text-rose-500">
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </TabsContent>

          {/* Reviews Tab */}
          <TabsContent value="reviews" className="mt-6">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-lg font-bold">Manage Reviews</h2>
              <Button data-testid="add-review-btn" onClick={() => openReviewDialog()} className="bg-rose-700 hover:bg-rose-800 rounded-none text-sm">
                <Plus className="h-4 w-4 mr-1" /> Add Review
              </Button>
            </div>
            <div className="space-y-2">
              {reviews.map(review => (
                <div key={review.id} data-testid={`admin-review-${review.id}`} className="bg-zinc-900/30 border border-white/5 p-4">
                  <div className="flex justify-between items-start gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="text-white text-sm font-bold">{review.reviewer_name}</p>
                        <span className="text-rose-700 text-xs">for '{review.book_title}'</span>
                      </div>
                      <p className="text-zinc-400 text-sm mt-1 line-clamp-2">"{review.quote}"</p>
                    </div>
                    <div className="flex gap-1 flex-shrink-0">
                      <Button data-testid={`edit-review-${review.id}`} variant="ghost" size="icon" onClick={() => openReviewDialog(review)} className="h-8 w-8 text-zinc-500 hover:text-white">
                        <Pencil className="h-3.5 w-3.5" />
                      </Button>
                      <Button data-testid={`delete-review-${review.id}`} variant="ghost" size="icon" onClick={() => deleteReview(review.id)} className="h-8 w-8 text-zinc-500 hover:text-rose-500">
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </TabsContent>

          {/* Messages Tab */}
          <TabsContent value="messages" className="mt-6">
            <h2 className="text-lg font-bold mb-6">Contact Messages</h2>
            {messages.length === 0 && <p className="text-zinc-600 text-center py-12">No messages yet.</p>}
            <div className="space-y-2">
              {messages.map(msg => (
                <div key={msg.id} data-testid={`admin-message-${msg.id}`} className="bg-zinc-900/30 border border-white/5 p-4">
                  <div className="flex justify-between items-start">
                    <div>
                      <p className="text-white text-sm font-bold">{msg.name}</p>
                      <p className="text-zinc-500 text-xs">{msg.email}</p>
                    </div>
                    <p className="text-zinc-600 text-xs">{new Date(msg.created_at).toLocaleDateString()}</p>
                  </div>
                  <p className="text-zinc-400 text-sm mt-2">{msg.message}</p>
                </div>
              ))}
            </div>
          </TabsContent>
        </Tabs>
      </div>

      {/* Book Dialog */}
      <Dialog open={bookDialog} onOpenChange={setBookDialog}>
        <DialogContent className="bg-zinc-950 border-white/10 text-white max-w-lg rounded-none">
          <DialogHeader>
            <DialogTitle className="font-display">{editingBook ? "Edit Book" : "Add New Book"}</DialogTitle>
            <DialogDescription className="text-zinc-500">Fill in the book details below.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-2 max-h-[60vh] overflow-y-auto pr-2">
            <div>
              <Label className="text-zinc-400 text-xs uppercase tracking-widest">Title</Label>
              <Input data-testid="book-title-input" value={bookForm.title} onChange={e => setBookForm({ ...bookForm, title: e.target.value })} className="mt-1 bg-zinc-900/50 border-white/10 text-white rounded-none" />
            </div>
            <div>
              <Label className="text-zinc-400 text-xs uppercase tracking-widest">Series</Label>
              <Select value={bookForm.series} onValueChange={v => setBookForm({ ...bookForm, series: v })}>
                <SelectTrigger data-testid="book-series-select" className="mt-1 bg-zinc-900/50 border-white/10 text-white rounded-none">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-zinc-950 border-white/10 text-white">
                  <SelectItem value="Belfast Murders">Belfast Murders</SelectItem>
                  <SelectItem value="Galway Murders">Galway Murders</SelectItem>
                  <SelectItem value="Others">Others</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-zinc-400 text-xs uppercase tracking-widest">Description</Label>
              <Textarea data-testid="book-description-input" value={bookForm.description} onChange={e => setBookForm({ ...bookForm, description: e.target.value })} className="mt-1 bg-zinc-900/50 border-white/10 text-white rounded-none min-h-[80px]" />
            </div>
            <div>
              <Label className="text-zinc-400 text-xs uppercase tracking-widest">Cover Image URL</Label>
              <Input data-testid="book-cover-input" value={bookForm.cover_url} onChange={e => setBookForm({ ...bookForm, cover_url: e.target.value })} className="mt-1 bg-zinc-900/50 border-white/10 text-white rounded-none" />
              {bookForm.cover_url && <img src={bookForm.cover_url} alt="Preview" className="mt-2 w-20 h-28 object-cover border border-white/10" />}
            </div>
            <div>
              <Label className="text-zinc-400 text-xs uppercase tracking-widest">Amazon Link</Label>
              <Input data-testid="book-amazon-input" value={bookForm.amazon_link} onChange={e => setBookForm({ ...bookForm, amazon_link: e.target.value })} className="mt-1 bg-zinc-900/50 border-white/10 text-white rounded-none" />
            </div>
            <div>
              <Label className="text-zinc-400 text-xs uppercase tracking-widest">Display Order</Label>
              <Input data-testid="book-order-input" type="number" value={bookForm.order} onChange={e => setBookForm({ ...bookForm, order: parseInt(e.target.value) || 0 })} className="mt-1 bg-zinc-900/50 border-white/10 text-white rounded-none w-24" />
            </div>
            <div className="flex items-center gap-3">
              <Switch data-testid="book-latest-switch" checked={bookForm.is_latest} onCheckedChange={v => setBookForm({ ...bookForm, is_latest: v })} />
              <Label className="text-zinc-400 text-sm">Mark as Latest Release</Label>
            </div>
          </div>
          <div className="flex justify-end gap-2 mt-4">
            <Button variant="ghost" onClick={() => setBookDialog(false)} className="text-zinc-400 rounded-none">Cancel</Button>
            <Button data-testid="save-book-btn" onClick={saveBook} className="bg-rose-700 hover:bg-rose-800 rounded-none">Save Book</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Review Dialog */}
      <Dialog open={reviewDialog} onOpenChange={setReviewDialog}>
        <DialogContent className="bg-zinc-950 border-white/10 text-white max-w-lg rounded-none">
          <DialogHeader>
            <DialogTitle className="font-display">{editingReview ? "Edit Review" : "Add New Review"}</DialogTitle>
            <DialogDescription className="text-zinc-500">Fill in the review details below.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div>
              <Label className="text-zinc-400 text-xs uppercase tracking-widest">Reviewer Name</Label>
              <Input data-testid="review-name-input" value={reviewForm.reviewer_name} onChange={e => setReviewForm({ ...reviewForm, reviewer_name: e.target.value })} className="mt-1 bg-zinc-900/50 border-white/10 text-white rounded-none" />
            </div>
            <div>
              <Label className="text-zinc-400 text-xs uppercase tracking-widest">Book Title</Label>
              <Input data-testid="review-book-input" value={reviewForm.book_title} onChange={e => setReviewForm({ ...reviewForm, book_title: e.target.value })} className="mt-1 bg-zinc-900/50 border-white/10 text-white rounded-none" />
            </div>
            <div>
              <Label className="text-zinc-400 text-xs uppercase tracking-widest">Quote</Label>
              <Textarea data-testid="review-quote-input" value={reviewForm.quote} onChange={e => setReviewForm({ ...reviewForm, quote: e.target.value })} className="mt-1 bg-zinc-900/50 border-white/10 text-white rounded-none min-h-[120px]" />
            </div>
          </div>
          <div className="flex justify-end gap-2 mt-4">
            <Button variant="ghost" onClick={() => setReviewDialog(false)} className="text-zinc-400 rounded-none">Cancel</Button>
            <Button data-testid="save-review-btn" onClick={saveReview} className="bg-rose-700 hover:bg-rose-800 rounded-none">Save Review</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
